import * as React from "react";
import * as Messages from "../../codegen/Messages";
import {
  cidrHasHostBitsSet,
  cidrPrefixWithinRange,
  cidrRange,
  cidrValid,
  getOverlapCidrs,
  isValidAddress,
  isValidIpAddress,
  maxIpv4CidrPrefix,
  minIpv4CidrPrefix,
} from "../../helpers/networkHelper";
import { uniqueGUID } from "../../helpers/util";
import { FormContext, FormState } from "./Form";
import { buildTextInputTestIds, TextInput, TextInputProps, TextInputTestIds } from "./TextInput";

export interface PrefixRange {
  /**
   * The numerical max value of the CIDR prefix (ex. /23)
   */
  max: number;
  /**
   * The numerical min value of the CIDR prefix (ex. /16)
   */
  min?: number;
}

export interface Validation {
/**
   * Flag that determines whether setting the bits of
   * the host address is treated as an invalid CIDR
   * Only has an effect if containsCidrBlock is set
   * @default false
   */
  allowHostBits?: boolean;
  /**
   * Controls whether validation allows CIDR blocks to entered
   * Works inclusivley with containsIpAddress
   * One of containsCidrBlock or containsIpAddress must be true
   */
  containsCidrBlock: boolean;
  /**
   * Controls whether validation allows single IP addresses to be entered
   * Works inclusivley with containsCidrBlock
   * One of containsCidrBlock or containsIpAddress must be true
   */
  containsIpAddress: boolean;
  /**
   * Accepts CIDRs and IP addresses
   * We assume that the list of CIDRs/IP addresses provided do not overlap with themselves
   * The only difference between this and reservedAddresses is the error message displayed
   */
  excludeAddresses?: string[];
  /**
   * The numberical range of the CIDR prefix
   * Only validates if containsCidrBlock is set
   */
  prefixRange?: PrefixRange;
  /**
   * Accepts CIDRs and IP addresses
   * We assume that the list of CIDRs/IP addresses provided do not overlap with themselves
   * The only difference between this and excludeAddresses is the error message displayed
   */
  reservedAddresses?: string[];
}

type BaseIpInputProps = Omit<TextInputProps,
"textType"
| "revealPassword"
| "revealPasswordAriaLabel"
| "multiline"
>;

export interface IpInputProps extends BaseIpInputProps {
  validation?: Validation;
}

export type IpInputTestIds = TextInputTestIds;
export const buildIpInputTestIds = buildTextInputTestIds;

export const IpInput = ({
  validation,
  validator,
  ...textInputProps
}: IpInputProps): JSX.Element => {
  const {
    allowHostBits = false,
    containsCidrBlock,
    containsIpAddress,
    excludeAddresses,
    prefixRange,
    reservedAddresses,
  } = validation ?? {};

  if (!containsCidrBlock && !containsIpAddress) {
    throw new Error("Either 'containsCidrBlock' or 'containsIpAddress' must be set");
  }

  const form: FormState = React.useContext(FormContext);

  if (!Object.keys(form).length) {
    throw new Error("IpInput should be used within form");
  }

  const maxCidrPrefix = prefixRange?.max ?? maxIpv4CidrPrefix;
  const minCidrPrefix = prefixRange?.min ?? minIpv4CidrPrefix;

  const [renderKey, setRenderKey] = React.useState(uniqueGUID());

  React.useEffect(() => {
    if (excludeAddresses && excludeAddresses.length > 0) {
      setRenderKey(uniqueGUID());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(excludeAddresses)]);

  const validateReservedAddresses = (addresses: string[]): string[] | undefined => {
    const overlappingAddresses = getOverlapCidrs(addresses);

    if (overlappingAddresses.length > 0) {
      return overlappingAddresses.map(overlap => {
        // Runs if user enters IP/CIDR and the reserved address is a CIDR
        if (cidrValid(overlap.cidr2)) {
          const [startingIp, endingIp] = cidrRange(overlap.cidr2);
          return Messages.validation.addressReservedRange(overlap.cidr1, overlap.cidr2, startingIp, endingIp);
        }

        // Runs if user enters a CIDR and reserved address is an IP address
        if (cidrValid(overlap.cidr1)) {
          return Messages.validation.cidrHasReservedIp(overlap.cidr1, overlap.cidr2);
        }

        // Runs if the reserved address and the user input are IP addresses
        return Messages.validation.addressReserved(overlap.cidr1);
      });
    }

    return undefined;
  };

  const validateExcludedAddresses = (addresses: string[]): string[] | undefined => {
    const overlappingAddresses = getOverlapCidrs(addresses);

    if (overlappingAddresses.length > 0) {
      return overlappingAddresses.map(overlap => Messages.validation.addressOverlap(
        overlap.cidr1,
        overlap.cidr2,
      ));
    }

    return undefined;
  };

  const validateAddress = (address: string): string[] | undefined => {
    if (!isValidAddress(address)) return [Messages.validation.addressInvalid(address)];
    return undefined;
  };

  const validateCidr = (cidr: string): string[] | undefined => {
    if (!cidrValid(cidr)) return [Messages.validation.cidrInvalid(cidr)];
    return undefined;
  };

  const validateIpAddress = (ipAddress: string): string[] | undefined => {
    if (!isValidIpAddress(ipAddress)) return [Messages.validation.ipAddressInvalid(ipAddress)];
    return undefined;
  };

  const validateCidrPrefix = (cidr: string): string[] | undefined => {
    if (!cidrPrefixWithinRange(cidr, minCidrPrefix, maxCidrPrefix)) {
      return [Messages.validation.cidrPrefixInvalid(
        minCidrPrefix.toString(),
        maxCidrPrefix.toString(),
      )];
    }
    return undefined;
  };

  const validateHostBits = (cidr: string): string[] | undefined => {
    if (cidrHasHostBitsSet(cidr)) return [Messages.validation.cidrHostBitsSet()];
    return undefined;
  };

  const fieldValidation = (address: string): string[] | undefined => {
    const errors: string[] = [];

    // Leave required validation up to the TextInput
    if (address) {
      if (containsCidrBlock && containsIpAddress) {
        errors.push(...(validateAddress(address) || []));
      } else if (containsCidrBlock) {
        errors.push(...(validateCidr(address) || []));
      } else if (containsIpAddress) {
        errors.push(...(validateIpAddress(address) || []));
      }

      // ensure it is a valid address (based on previous validation results)
      if (errors.length === 0) {
        // only run if it is a CIDR and is enabled
        if (containsCidrBlock && cidrValid(address)) {
          errors.push(...(validateCidrPrefix(address) || []));
        }
      }

      if (errors.length === 0) {
        if (containsCidrBlock && cidrValid(address) && !allowHostBits) {
          errors.push(...(validateHostBits(address) || []));
        }
      }

      if (errors.length === 0) {
        if (excludeAddresses) {
          errors.push(...(validateExcludedAddresses([address, ...excludeAddresses])) || []);
        }

        if (reservedAddresses) {
          errors.push(...(validateReservedAddresses([address, ...reservedAddresses]) || []));
        }
      }
    }

    errors.push(...(validator?.(address) || []));

    return errors.length > 0 ? errors : undefined;
  };

  return (
    <TextInput
      key={renderKey}
      validator={fieldValidation}
      {...textInputProps}
    />
  );
};
